import { useEffect, useState } from 'react';
import { AssignedUserSelect } from '@/components/AssignedUserSelect';
import { isAssignableAssignedUser } from '@/components/assigned-user.utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { UserOption } from '@/otros-tramites/services/otrosTramitesService';

interface QuickAssignUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityLabel: string;
  initialAssignedUser: UserOption | null;
  isSubmitting?: boolean;
  onSubmit: (assignedUser: UserOption | null) => Promise<void> | void;
}

export function QuickAssignUserDialog({
  open,
  onOpenChange,
  entityLabel,
  initialAssignedUser,
  isSubmitting = false,
  onSubmit,
}: QuickAssignUserDialogProps) {
  const [selectedAssignedUser, setSelectedAssignedUser] = useState<UserOption | null>(initialAssignedUser);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedAssignedUser(initialAssignedUser);
  }, [initialAssignedUser, open]);

  const hasChanges =
    (initialAssignedUser?.id ?? null) !== (selectedAssignedUser?.id ?? null);

  const handleSubmit = async () => {
    await onSubmit(selectedAssignedUser);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] min-w-0 max-w-md overflow-visible">
        <DialogHeader>
          <DialogTitle>{initialAssignedUser ? 'Editar responsable' : 'Asignar responsable'}</DialogTitle>
          <DialogDescription>
            {`Seleccioná el usuario responsable para ${entityLabel}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-3">
          <AssignedUserSelect
            className="min-w-0 max-w-full"
            value={selectedAssignedUser}
            onChange={setSelectedAssignedUser}
            placeholder="Buscar usuario responsable"
            ariaLabel="Usuario asignado"
            disabled={isSubmitting}
            filterOption={isAssignableAssignedUser}
          />

          {selectedAssignedUser && (
            <Button
              type="button"
              variant="ghost"
              className="px-0 text-sm text-muted-foreground hover:text-foreground"
              onClick={() => setSelectedAssignedUser(null)}
              disabled={isSubmitting}
            >
              Dejar sin asignado
            </Button>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting || !hasChanges}
          >
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default QuickAssignUserDialog;
